import { ThemeBus } from '@theme/events-bus';
import { EVENTS } from '@theme/event-constants';

class InfiniteScroll extends HTMLElement {
  #observer = null;
  #abortController = null;
  #loading = false;
  #done = false;

  connectedCallback() {
    if (this.#done || !this.dataset.nextUrl) {
      this.remove();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#loadNextPage();
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  async #loadNextPage() {
    if (this.#loading || this.#done) return;

    const nextUrl = this.dataset.nextUrl;
    const sectionId = this.dataset.sectionId;
    if (!nextUrl || !sectionId) {
      this.#finish();
      return;
    }

    this.#loading = true;
    this.setAttribute('aria-busy', 'true');
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load next page: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const currentGrid = this.#getProductGrid(document);
      const nextGrid = this.#getProductGrid(doc);

      if (!currentGrid || !nextGrid) {
        this.#finish();
        return;
      }

      const existingIds = new Set(
        Array.from(currentGrid.querySelectorAll(':scope > [data-product-id]')).map(
          (el) => el.getAttribute('data-product-id')
        )
      );

      const fragment = document.createDocumentFragment();
      nextGrid.querySelectorAll(':scope > [data-product-id]').forEach((node) => {
        const productId = node.getAttribute('data-product-id');
        if (!productId || existingIds.has(productId)) return;
        existingIds.add(productId);
        fragment.appendChild(document.importNode(node, true));
      });

      if (fragment.childNodes.length) {
        currentGrid.appendChild(fragment);

        if (window.ScrollAnimations?.setupStaggerContainers) {
          window.ScrollAnimations.setupStaggerContainers();
        }

        ThemeBus.dispatchEvent(new Event(EVENTS.gridUpdated));
      }

      const nextTrigger = doc.querySelector('infinite-scroll[data-next-url]');
      const followingUrl = nextTrigger?.getAttribute('data-next-url');

      if (followingUrl) {
        this.dataset.nextUrl = followingUrl;
      } else {
        this.#finish();
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error(error);
    } finally {
      this.#loading = false;
      if (!this.#done) {
        this.setAttribute('aria-busy', 'false');
      }
    }
  }

  #getProductGrid(root) {
    return root.querySelector('[data-product-grid]') || root.querySelector('.product-list [class*="grid"]');
  }

  #finish() {
    this.#done = true;
    this.#observer?.disconnect();
    this.#observer = null;
    this.setAttribute('aria-busy', 'false');
    this.remove();
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
